import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '@design/core.scss';
import '@design/list.scss';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AutoSuggest } from '../auto-suggest/auto-suggest';
import { useFirstRender } from '../common/effects/useFirstRender';
import { TagItem } from './tag-item';
import styles from './tags.module.scss';
const Tags = ({ disabled = false, items = [], maxTags = Number.MAX_VALUE, onChange, readonly = false, size = 'sm', tagStyle = 'default', tagWidth = 60, style = {}, suggestions = [], RTL = false, placeholder = '', focusable = true, accent = 'flat', wrap = true, tagHeight = null, }) => {
    // STATES
    const [tagItems, setTagItems] = useState(items
        .map(({ name, disabled }) => ({
        accent,
        disabled: disabled,
        id: nanoid(),
        markedForRemoval: false,
        name: name,
        readonly,
    }))
        .slice(0, maxTags));
    const isFirstRender = useFirstRender();
    const [inputValue, setInputValue] = useState('');
    const inputRef = React.useRef(null);
    const canAdd = useMemo(() => tagItems.length + 1 <= maxTags && !readonly, [tagItems.length, inputValue]);
    const tagSuggestions = useMemo(() => suggestions.map(suggestion => ({
        name: suggestion,
        value: suggestion,
    })), []);
    // HANDLERS
    const handleKeyUp = useCallback((ev) => {
        if (ev.key === 'Enter' && canAdd && inputRef.current) {
            const val = inputRef.current.getValue().trim();
            if (val) {
                handleAdd(val);
            }
        }
    }, [canAdd, inputValue]);
    const handleChange = useCallback((val) => {
        val && setInputValue(val.trim());
    }, []);
    const handleAdd = useCallback((value) => {
        if (canAdd) {
            const _value = typeof value === 'string' ? value : value.name;
            setTagItems(prev => prev.concat({
                accent,
                id: nanoid(),
                name: _value,
            }));
            if (inputRef.current) {
                inputRef.current.setValue('');
                inputRef.current.focus();
            }
        }
    }, [canAdd]);
    const handleRemove = useCallback((val) => {
        setTagItems(tags => tags.filter(tag => tag.id !== val));
        inputRef.current?.focus();
    }, []);
    // EFFECTS
    useEffect(() => {
        if (onChange && !isFirstRender.current) {
            onChange(tagItems.map(tag => tag.name));
        }
    }, [tagItems.length]);
    useEffect(() => {
        if (!isFirstRender.current) {
            setTagItems(items
                .map(item => ({
                accent,
                disabled: item.disabled,
                id: nanoid(),
                name: item.name,
                readonly: readonly,
            }))
                .slice(0, maxTags));
        }
    }, [items.length]);
    return (_jsxs("ul", { className: classNames(styles.tags_wrapper, {
            [styles.tags_disabled]: disabled,
            [styles.tags_rtl]: RTL,
            [styles.tags_wrap]: wrap,
        }), style: Object.assign({}, style, tagHeight
            ? {
                '--tag-height': `${tagHeight}px`,
            }
            : {}), children: [tagItems.map(({ id, name, disabled, readonly, markedForRemoval }) => (_jsx(TagItem, { id: id, disabled: disabled, readonly: readonly, handleRemove: handleRemove, name: name, tagWidth: tagWidth, tagStyle: tagStyle, size: size, markedForRemoval: markedForRemoval, focusable: focusable, accent: accent }, id))), canAdd && (_jsx("li", { className: styles.tags_input_wrapper, children: _jsx(AutoSuggest, { suggestions: tagSuggestions, onChange: handleChange, onSelection: handleAdd, onKeyUp: handleKeyUp, value: inputValue, placeholder: placeholder, focusable: focusable, accent: accent, ref: inputRef, size: size, disableIcon: !suggestions.length }) }))] }));
};
Tags.displayName = 'Tags';
export { Tags };
